import { useState } from 'react';
import { useSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import {
  TaskProposal,
  ProfileSuggestion,
  TaskProposalEdits,
  useAcceptSuggestionMutation,
  useRejectSuggestionMutation,
  useAcceptTaskProposalMutation,
  useRejectTaskProposalMutation,
} from 'src/services/ai/ai-enrichment';

import Iconify from 'src/components/iconify';

import TaskProposalDialog from './task-proposal-dialog';
import { formatDue, TASK_TYPE_LABELS, formatFindingValue } from './format-finding';

/**
 * Pending suggestions and proposed tasks with their accept / reject / edit actions (JT1-1764,
 * JT1-1766). Shared by the record's AI suggestions card and the note's "N suggestions" popover, so a
 * decision made in one shows in the other (both read the same cached findings).
 */

const errorText = (error: any) => error?.data?.message ?? error?.message ?? 'Something went wrong. Please try again.';

export function SuggestionRow({ suggestion, showNoteDate }: { suggestion: ProfileSuggestion; showNoteDate?: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const [accept, { isLoading: accepting }] = useAcceptSuggestionMutation();
  const [reject, { isLoading: rejecting }] = useRejectSuggestionMutation();

  const run = async (action: 'accept' | 'reject') => {
    try {
      const result = await (action === 'accept' ? accept(suggestion) : reject(suggestion)).unwrap();
      if (result.status === 'superseded') {
        enqueueSnackbar(`${suggestion.label} has changed since this was suggested, so it was left as it is.`, { variant: 'info' });
      }
    } catch (error) {
      enqueueSnackbar(errorText(error), { variant: 'error' });
    }
  };

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2">{suggestion.label}</Typography>
        {showNoteDate && (
          <Typography variant="caption" color="text.disabled">
            from a note, {new Date(suggestion.created_at).toLocaleDateString()}
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
        {formatFindingValue(suggestion.field, suggestion.before)}
      </Typography>
      <Typography variant="body2">{formatFindingValue(suggestion.field, suggestion.after)}</Typography>
      <Stack direction="row" gap={1} sx={{ pt: 0.5 }}>
        <Button size="small" variant="soft" color="primary" disabled={accepting || rejecting} onClick={() => run('accept')}>
          Accept
        </Button>
        <Button size="small" color="inherit" disabled={accepting || rejecting} onClick={() => run('reject')}>
          Reject
        </Button>
      </Stack>
    </Stack>
  );
}

export function TaskProposalRow({ proposal }: { proposal: TaskProposal }) {
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<TaskProposal | null>(null);
  const [accept, { isLoading: accepting }] = useAcceptTaskProposalMutation();
  const [reject, { isLoading: rejecting }] = useRejectTaskProposalMutation();

  const doAccept = async (edits?: TaskProposalEdits) => {
    try {
      await accept({ id: proposal.id, edits }).unwrap();
      setEditing(null);
      enqueueSnackbar('Task added.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(errorText(error), { variant: 'error' });
    }
  };

  const doReject = async () => {
    try {
      await reject(proposal.id).unwrap();
    } catch (error) {
      enqueueSnackbar(errorText(error), { variant: 'error' });
    }
  };

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Iconify icon="solar:checklist-minimalistic-bold" width={18} sx={{ color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="subtitle2">{proposal.title}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {TASK_TYPE_LABELS[proposal.type] ?? proposal.type} · {formatDue(proposal.due_at, proposal.due_has_time)}
        {proposal.assignee?.name ? ` · ${proposal.assignee.name}` : ''}
      </Typography>
      <Stack direction="row" gap={1} sx={{ pt: 0.5 }}>
        <Button size="small" variant="soft" color="primary" disabled={accepting || rejecting} onClick={() => doAccept()}>
          Add task
        </Button>
        <Button size="small" color="inherit" disabled={accepting || rejecting} onClick={() => setEditing(proposal)}>
          Edit
        </Button>
        <Button size="small" color="inherit" disabled={accepting || rejecting} onClick={doReject}>
          Reject
        </Button>
      </Stack>
      <TaskProposalDialog proposal={editing} onClose={() => setEditing(null)} onAccept={doAccept} saving={accepting} />
    </Stack>
  );
}

/** Values the AI filled in by itself, so a recruiter can see where they came from. */
export function FilledByAi({ filled }: { filled: ProfileSuggestion[] }) {
  if (!filled.length) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">Filled in by AI from notes</Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {filled.map((row) => (
          <Stack key={row.id} direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Chip label="AI" size="small" color="info" variant="soft" />
            <Typography variant="body2">
              <strong>{row.label}:</strong> {formatFindingValue(row.field, row.after)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function FindingsList({ suggestions, tasks, showNoteDate }: { suggestions: ProfileSuggestion[]; tasks: TaskProposal[]; showNoteDate?: boolean }) {
  return (
    <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}>
      {suggestions.map((suggestion) => (
        <SuggestionRow key={suggestion.id} suggestion={suggestion} showNoteDate={showNoteDate} />
      ))}
      {tasks.map((proposal) => (
        <TaskProposalRow key={proposal.id} proposal={proposal} />
      ))}
    </Stack>
  );
}
